// Timesheets settings (round 44). Admin only.
//
// Four things: the switch (timesheets ship off), the day the week starts,
// the leave and allowance lists, and who fills in a timesheet. Hours only:
// nothing here sets a pay rate, and the copy says so.
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type User } from '../api';
import { useSession } from '../session';
import { Banner } from '../ui';
import { WEEKDAY_NAMES } from '../lib/timesheetWeeks';
import { timesheets, type TypeRow } from '../timesheetsApi';

export function TimesheetSettingsPage() {
  const { tenant, refresh } = useSession();
  if (!tenant) return null;
  return (
    <section>
      <h1>Timesheets</h1>
      <p className="hint">
        Your people fill in their hours each week on their phone and sign them; a manager approves them or sends
        them back, and you export them for payroll. Dayboard records and totals hours only. It holds no pay rates and
        applies no award: your payroll software does that from the export.
      </p>
      <SwitchPanel onSaved={refresh} />
      {tenant.timesheetsEnabled && (
        <>
          <WeekStartPanel onSaved={refresh} />
          <TypesPanel
            title="Kinds of leave"
            intro="What a day can be marked as instead of, or beside, hours worked. The names come out in the export for your bookkeeper to match."
            api={timesheets.leaveTypes}
            placeholder="Jury duty"
          />
          <TypesPanel
            title="Allowances"
            intro="Ticked against a day, like travel or a meal. Named only: what each one pays is set in your payroll software."
            api={timesheets.allowanceTypes}
            placeholder="Tool allowance"
          />
          <PeoplePanel />
        </>
      )}
    </section>
  );
}

function useCompanySave(onSaved: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  async function save(body: Parameters<typeof api.updateCompany>[0]) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateCompany(body);
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that. Try again.');
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, saved, save };
}

function SwitchPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const { busy, error, save } = useCompanySave(onSaved);
  if (!tenant) return null;
  const on = tenant.timesheetsEnabled;
  return (
    <section className="panel">
      <h2>Use timesheets</h2>
      {error && <Banner kind="error">{error}</Banner>}
      <p>
        {on
          ? 'Timesheets are on. Your field workers see My timesheet in their menu; managers and admins see everyone’s.'
          : 'Timesheets are off. Nobody in your company sees them until you turn them on.'}
      </p>
      {on && (
        <p className="hint">
          Turning timesheets off hides them from everyone and stops the reminder emails. Nothing is deleted: the weeks
          already entered are kept, still come out in your data export, and come back when you turn timesheets on
          again.
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className={on ? 'btn-secondary' : 'btn-primary'}
          disabled={busy}
          onClick={() => void save({ timesheetsEnabled: !on })}
        >
          {on ? 'Turn timesheets off' : 'Turn timesheets on'}
        </button>
      </div>
    </section>
  );
}

function WeekStartPanel({ onSaved }: { onSaved: () => Promise<void> }) {
  const { tenant } = useSession();
  const [day, setDay] = useState(tenant?.timesheetWeekStart ?? 1);
  const { busy, error, saved, save } = useCompanySave(onSaved);
  if (!tenant) return null;
  const dirty = day !== tenant.timesheetWeekStart;
  return (
    <section className="panel">
      <h2>Your pay week</h2>
      {error && <Banner kind="error">{error}</Banner>}
      {saved && !dirty && <Banner kind="ok">Saved.</Banner>}
      <div className="row">
        <label htmlFor="week-start">
          A timesheet week starts on
          <select id="week-start" className="form-input" value={day} onChange={(e) => setDay(Number(e.target.value))}>
            {WEEKDAY_NAMES.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="hint">
        Match it to your pay week. A change applies to weeks not started yet; weeks already started keep their dates.
        People are reminded by email the morning after a week ends if theirs is not in.
      </p>
      <button type="button" className="btn-primary mt-4" disabled={busy || !dirty} onClick={() => void save({ timesheetWeekStart: day })}>
        Save
      </button>
    </section>
  );
}

function TypesPanel({
  title,
  intro,
  api: list,
  placeholder,
}: {
  title: string;
  intro: string;
  api: typeof timesheets.leaveTypes;
  placeholder: string;
}) {
  const [rows, setRows] = useState<TypeRow[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await list.list(showArchived)).types);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the list.');
    }
  }, [list, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>{title}</h2>
      <p className="hint">{intro}</p>
      {error && <Banner kind="error">{error}</Banner>}
      {rows === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rows.map((row) => (
            <li key={row.id} className="flex min-h-11 flex-wrap items-center justify-between gap-2 py-1">
              <span className={row.archived ? 'text-gray-500' : 'text-gray-900'}>
                {row.name}
                {row.archived && <span className="muted"> (archived)</span>}
              </span>
              <span className="flex gap-3">
                <button
                  type="button"
                  className="row-action"
                  disabled={busy}
                  onClick={() => {
                    const next = window.prompt(`Rename ${row.name}`, row.name);
                    if (next && next.trim() !== '' && next !== row.name) void act(() => list.rename(row.id, next.trim()));
                  }}
                >
                  Rename
                </button>
                <button type="button" className="row-action" disabled={busy} onClick={() => void act(() => list.archive(row.id, !row.archived))}>
                  {row.archived ? 'Restore' : 'Archive'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <label className="mt-2 flex min-h-11 items-center gap-3 text-sm">
        <input type="checkbox" className="h-5 w-5" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
        Show archived
      </label>
      <form
        className="row mt-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() === '') return;
          void act(() => list.create(name.trim())).then((ok) => ok && setName(''));
        }}
      >
        <label htmlFor={`add-${title}`}>
          Add one
          <input
            id={`add-${title}`}
            className="form-input"
            maxLength={80}
            placeholder={placeholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-secondary" disabled={busy || name.trim() === ''}>
          Add
        </button>
      </form>
      <p className="hint">Archive one you no longer use: it stops being offered and still names the days that have it.</p>
    </section>
  );
}

const ROLE_WORD: Record<User['role'], string> = { admin: 'Admin', manager: 'Manager', field: 'Field worker' };

function PeoplePanel() {
  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers((await api.listUsers()).users.filter((u) => u.status !== 'deactivated'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your people.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function set(user: User, value: string) {
    setBusy(user.id);
    setError(null);
    try {
      await api.updateUser(user.id, { onTimesheets: value === 'default' ? null : value === 'on' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <h2>Who fills in a timesheet</h2>
      <p className="hint">
        Field workers do and managers and admins do not, unless you say otherwise here. Somebody switched off is not
        reminded and does not show as missing.
      </p>
      {error && <Banner kind="error">{error}</Banner>}
      {users === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {users.map((user) => {
            const setting = user.onTimesheetsSetting ?? null;
            const byDefault = user.role === 'field';
            return (
              <li key={user.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="min-w-0">
                  <span className="text-gray-900">{user.name}</span>
                  <span className="muted"> · {ROLE_WORD[user.role]}</span>
                </span>
                <select
                  aria-label={`Timesheets for ${user.name}`}
                  className="form-input w-auto"
                  disabled={busy === user.id}
                  value={setting === null ? 'default' : setting ? 'on' : 'off'}
                  onChange={(e) => void set(user, e.target.value)}
                >
                  <option value="default">Default ({byDefault ? 'fills one in' : 'does not'})</option>
                  <option value="on">Fills one in</option>
                  <option value="off">Does not</option>
                </select>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
